// Renders composer progress, error, and resolved states.

import {
  Check,
  Clock3,
  ChevronDown,
  CircleAlert,
  CircleFadingPlus,
  Brain,
  ListTree,
  LoaderCircle,
  Search,
  SquarePen,
  X,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { TruncatedText } from '@/components/shared/truncated-text';
import { cn } from '@/lib/utils';
import { CollapseBody } from './collapse-toggle';
import { Astroid } from './icons';
import type { TicketOutcome, TraceStep } from './types';

// No stop buttons here: blueprint and response generation have no abort API.

/** Uppercase mono label used by every progress bar. */
function BarLabel({ text }: { text: string }) {
  return <span className="font-mono text-[12px] font-medium uppercase tracking-[0.06em] text-text-disabled">{text}</span>;
}

/** Three staggered dots at the right edge of an in-progress bar (per Figma). */
function ProgressDots() {
  return (
    <span className="flex shrink-0 items-center gap-1">
      {[0, 1, 2].map((dot) => (
        <span
          key={dot}
          className="size-[4px] animate-bounce rounded-full bg-gray-500"
          style={{ animationDelay: `${dot * 150}ms` }}
        />
      ))}
    </span>
  );
}

export function ActionRunningBar({ label }: { label: string }) {
  return (
    <div className="flex w-full items-center justify-between rounded-2xl border border-gray-500 bg-white px-4 py-4">
      <div className="flex items-center gap-2.5">
        <Astroid filled className="size-[13px] text-purple-500" />
        <BarLabel text={label} />
      </div>
      <ProgressDots />
    </div>
  );
}

const TRACE_ICONS = {
  lookup: Search,
  policy: Brain,
  compose: Astroid,
} as const;

/** Trace step row: icon + title with the detail below; a faint connector drops toward the next step. */
export function TraceStepRow({ step, showConnector = false }: { step: TraceStep; showConnector?: boolean }) {
  const Icon = TRACE_ICONS[step.kind];
  return (
    <div className="flex min-w-0 items-stretch gap-2.5">
      <div className="relative flex flex-col items-center">
        <Icon className="mt-0.5 size-[14px] shrink-0 text-gray-800" />
        {/* Extends through the list gap so the line reads continuous, per Figma. */}
        {showConnector && <span className="absolute -bottom-2.5 top-[17px] w-[0.5px] bg-gray-500" />}
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-[12px] leading-4 text-gray-900">{step.title}</span>
        {step.detail && (
          <span
            title={step.detail}
            className="max-w-full self-start truncate rounded-lg text-[11px] leading-4 text-gray-800"
          >
            {step.detail}
          </span>
        )}
      </div>
    </div>
  );
}

interface GeneratingCardProps {
  trace: TraceStep[];
  stepsDone: number;
  expanded: boolean;
  onToggleExpanded: () => void;
}

interface LiveProgressCardProps extends GeneratingCardProps {
  title: string;
  pendingLabel: string;
  marker: ReactNode;
}

function LiveProgressCard({ trace, stepsDone, expanded, onToggleExpanded, title, pendingLabel, marker }: LiveProgressCardProps) {
  const visibleSteps = trace.slice(0, stepsDone);
  // Steps can run in parallel, so the generic row only fills the gaps when none of them is running.
  const awaitingNextStep = visibleSteps.every((step) => step.done);

  return (
    <div className="flex w-full flex-col rounded-2xl border border-gray-500 bg-white p-4">
      <div className="flex w-full items-center justify-between">
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          className="flex cursor-pointer items-center gap-2.5 transition-opacity hover:opacity-70"
        >
          {marker}
          <BarLabel text={title} />
          <ChevronDown className={cn('size-[14px] text-gray-700 transition-transform duration-300', !expanded && '-rotate-90')} />
        </button>
        <ProgressDots />
      </div>
      {/* Stacked title + subtext per Figma — the inline chip style is only for the AI-chat trace. */}
      <CollapseBody open={expanded}>
        <div className="flex flex-col gap-2.5 pt-3">
          {visibleSteps.map((step, index) => {
            const isLast = index === visibleSteps.length - 1;
            const Icon = TRACE_ICONS[step.kind];
            return (
              <div key={step.id} className="flex items-stretch gap-2.5">
                <div className="relative flex flex-col items-center">
                  {!step.done ? (
                    <LoaderCircle className="mt-0.5 size-[14px] shrink-0 animate-spin text-gray-900" />
                  ) : (
                    <Icon className="mt-0.5 size-[14px] shrink-0 text-gray-900" />
                  )}
                  {/* Extends through the list gap so the line reads continuous, per Figma. */}
                  {(!isLast || awaitingNextStep) && <span className="absolute -bottom-2.5 top-[17px] w-[0.5px] bg-gray-500" />}
                </div>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <p className="text-[13px] leading-[18px] text-text-body">{step.title}</p>
                  {step.detail && <p className="text-[11px] leading-4 text-gray-800">{step.detail}</p>}
                </div>
              </div>
            );
          })}
          {/* Covers the wait before the first step and the beat after one ends, so the card is never idle. */}
          {awaitingNextStep && (
            <div className="flex items-center gap-2.5">
              <LoaderCircle className="size-[14px] animate-spin text-gray-900" />
              <p className="text-[13px] leading-[18px] text-text-body">{pendingLabel}</p>
            </div>
          )}
        </div>
      </CollapseBody>
    </div>
  );
}

export function BlueprintLoadingBar(props: GeneratingCardProps) {
  return (
    <LiveProgressCard
      {...props}
      title="Generating a blueprint"
      pendingLabel="Generating blueprint"
      marker={<ListTree className="size-[14px] text-gray-800" />}
    />
  );
}

/** Single-line bar — Brahma emits no thinking steps while a blueprint refines. */
export function RefiningBlueprintBar() {
  return (
    <div className="flex w-full items-center justify-between rounded-2xl border border-gray-500 bg-white px-4 py-4">
      <div className="flex items-center gap-2.5">
        <CircleFadingPlus className="size-[14px] text-pink-500" />
        <BarLabel text="Refining blueprint based on feedback..." />
      </div>
      <ProgressDots />
    </div>
  );
}

export function GeneratingCard(props: GeneratingCardProps) {
  return (
    <LiveProgressCard
      {...props}
      title="Generating a response"
      pendingLabel="Generating response"
      marker={<Astroid filled className="size-[13px] text-purple-500" />}
    />
  );
}

interface ErrorBarProps {
  message: string;
  onReject?: () => void;
  /** Hand the ticket to a person. */
  onHandleManually?: () => void;
  /** Omitted when the failure cannot be retried — rejecting is then the only way forward. */
  onRetry?: () => void;
}

/** Why Kim handed the ticket to a person, folded like the automation card's "steps performed". */
export function HandoffReasonCard({ reason }: { reason: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex w-full flex-col rounded-2xl border border-gray-500 bg-white p-4">
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        className="flex cursor-pointer items-center gap-1 self-start rounded-md font-mono text-[11px] font-medium uppercase leading-4 text-text-disabled outline-none transition-opacity hover:opacity-70 focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        Why Kim handed this over
        <ChevronDown className={cn('size-3 text-gray-700 transition-transform duration-300', !open && '-rotate-90')} />
      </button>
      <CollapseBody open={open}>
        <p className="pt-2.5 text-[13px] leading-5 text-text-body">{reason}</p>
      </CollapseBody>
    </div>
  );
}

/** White single-line error bar per Figma — red message left, actions right. */
export function ErrorBar({ message, onReject, onHandleManually, onRetry }: ErrorBarProps) {
  return (
    <div className="flex w-full items-center justify-between gap-3 rounded-2xl border border-gray-500 bg-white px-4 py-2.5">
      <div className="flex min-w-0 items-center gap-2.5">
        <CircleAlert className="size-[14px] shrink-0 text-red-500" />
        {/* The whole message on hover, only when the bar cuts it short. */}
        <TruncatedText text={message} className="min-w-0 text-[13px] leading-[18px] text-red-500" />
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {onHandleManually ? (
          // A ticket handed to a person can still be given back: reject stays quiet, the hand-off leads.
          <>
            {onReject && (
              <Button variant="ghost" onClick={onReject} className="px-3 text-[13px] font-normal text-gray-900">
                <X className="size-[14px]" />
                Reject &amp; Unassign
              </Button>
            )}
            <Button variant="outline" onClick={onHandleManually} className="text-[13px] font-normal">
              <SquarePen className="size-[14px]" />
              Handle manually
            </Button>
          </>
        ) : (
          onReject && (
            <Button variant="ghost" onClick={onReject} className="px-3 text-[13px] font-normal text-gray-900">
              Reject &amp; Unassign ?
            </Button>
          )
        )}
        {onRetry && (
          <>
            <span className="h-4 w-px bg-gray-300" />
            <Button variant="outline" onClick={onRetry} className="text-[13px] font-normal">
              Retry ?
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/** Each ending reads differently — a handed-off ticket was never answered. */
const DONE_COPY: Record<'answered' | TicketOutcome, { text: string; handedOff?: boolean }> = {
  answered: { text: 'This ticket has been answered' },
  resolved: { text: 'This ticket has been resolved' },
  unassigned: { text: 'This ticket has been rejected and unassigned', handedOff: true },
  notPickedUp: { text: 'Sentinel did not pick up this ticket', handedOff: true },
};

export function DoneBar({ label }: { label: 'answered' | TicketOutcome }) {
  const { text, handedOff } = DONE_COPY[label];
  return (
    <div className="flex w-full items-center gap-2.5 rounded-2xl border border-gray-500 bg-white px-4 py-4">
      {handedOff ? (
        <X className="size-[14px] text-gray-800" />
      ) : (
        <Check className="size-[14px] text-emerald-500" />
      )}
      <BarLabel text={text} />
    </div>
  );
}

const AUTOMATION_STATUS_COPY = {
  sent: 'This ticket was answered by automation',
  sending: 'Automation is sending a response',
  blocked: 'Automation did not send a response',
  failed: 'Automation could not send the response',
  unknown: 'Automation response delivery is unknown',
} as const;

/**
 * The server's reasons for holding a reply back, in the agent's words. Anything else it records
 * is a raw error string meant for logs, and is not shown.
 */
const AUTOMATION_REASON: Record<string, string> = {
  'Draft message does not meet confidence threshold': 'Confidence was below the brand’s show-draft threshold',
  'Response blocked due to shadow mode': 'The brand is in shadow mode',
  'Draft not automated — nothing sent': 'Automation’s rules did not allow sending',
};

/** What the agent can do about a reply automation produced and did not deliver. */
const AUTOMATION_UNSENT_HINT: Partial<Record<keyof typeof AUTOMATION_STATUS_COPY, string>> = {
  blocked: 'The reply was not sent. Use it to review, edit and send it yourself.',
  failed: 'Delivery was not confirmed. Check the conversation first, so the customer is not answered twice.',
  unknown: 'Delivery was not confirmed. Check the conversation first, so the customer is not answered twice.',
};

export function AutomationStatusBar({
  delivery,
  detail,
}: {
  delivery: keyof typeof AUTOMATION_STATUS_COPY;
  /** Why automation stopped, as the server recorded it. */
  detail?: string | null;
}) {
  const pending = delivery === 'sending';
  const successful = delivery === 'sent';
  const Icon = pending ? LoaderCircle : successful ? Check : CircleAlert;
  const hint = AUTOMATION_UNSENT_HINT[delivery];
  const reason = detail ? AUTOMATION_REASON[detail.trim().replace(/\.$/, '')] : undefined;
  return (
    <div className="flex w-full flex-col gap-1.5 rounded-2xl border border-gray-500 bg-white px-4 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Icon
            className={cn(
              'size-[14px]',
              pending && 'animate-spin text-gray-800',
              successful && 'text-emerald-500',
              !pending && !successful && 'text-amber-600',
            )}
          />
          <BarLabel text={AUTOMATION_STATUS_COPY[delivery]} />
        </div>
        {pending && <ProgressDots />}
      </div>
      {hint && (
        <p className="pl-6 text-[13px] leading-[18px] text-gray-900">
          {reason ? `${reason}. ` : ''}
          {hint}
        </p>
      )}
    </div>
  );
}

export function WaitingForCustomerBar() {
  return (
    <div className="flex w-full items-center gap-2.5 rounded-2xl border border-gray-500 bg-white px-4 py-4">
      <Clock3 className="size-[14px] text-gray-800" />
      <BarLabel text="Waiting for customer reply" />
    </div>
  );
}
